<template>
  <ShortcutWrapper isDialog @arrow-left-shortcut="previous" @arrow-right-shortcut="next">
    <v-card id="tract-visit-evidence" style="display: flex; flex-flow: column; height: 90vh;">
      <v-card-title class="primary white--text">
        <span class="headline text-truncate">{{ current.name }}</span>
        <v-spacer/>
        <BaseDialogActions hideRefresh>
          <template #left>
            <Icon
            icon="mdi-download"
            iconColor="tertiary"
            :small="false"
            :large="true"
            :tooltipText="$t('downloadFile')"
            @icon-clicked="download(current)"/>
          </template>
        </BaseDialogActions>
      </v-card-title>
      <v-card-text class="pa-0 evidence-stage" style="flex: 1; min-height: 0; overflow: hidden; position: relative;">
        <ImageViewer
        v-if="current.isImage"
        :key="current.ref"
        :imageData="[current.ref]"
        height="100%"
        maxHeight="100%"
        draggable
        hideActions
        :annotations="false"/>
        <template v-else-if="current.isGpx">
          <v-row v-if="gpx.loading" class="fill-height ma-0" justify="center" align="center">
            <v-progress-circular indeterminate color="primary"/>
          </v-row>
          <TractVisitGpxMap v-else-if="gpx.features" :key="current.ref" :tract="tract" :features="gpx.features" height="100%"/>
          <v-row v-else class="fill-height ma-0" justify="center" align="center">
            <span class="subtitle-1">{{ gpx.error }}</span>
          </v-row>
        </template>
        <v-row v-else class="fill-height ma-0" justify="center" align="center">
          <span class="subtitle-1">{{ $t('noPreviewAvailable') }}</span>
        </v-row>

        <div v-if="files.length > 1" class="map-overlay-toggle-group evidence-nav">
          <Icon
          icon="mdi-chevron-left"
          @icon-clicked="previous"
          :tooltipText="$t('previous')"
          xLarge
          :small="false"
          iconColor="white"
          margin="map-option-button"
          tooltipColor="black"
          elementId="evidence-previous"/>
          <span class="map-option-button evidence-page">{{ page }} / {{ files.length }}</span>
          <Icon
          icon="mdi-chevron-right"
          @icon-clicked="next"
          :tooltipText="$t('next')"
          xLarge
          :small="false"
          iconColor="white"
          margin="map-option-button"
          tooltipColor="black"
          elementId="evidence-next"/>
        </div>
      </v-card-text>
    </v-card>
  </ShortcutWrapper>
</template>

<script>
import { saveAs } from 'file-saver'
import { gpxToGeojson } from '@/utils/map/Gpx'

export default {
  name: 'TractVisitEvidenceViewer',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ImageViewer: () => import('@/components/helper/ImageViewer.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue'),
    TractVisitGpxMap: () => import('@/components/tract/tract-visits/TractVisitGpxMap.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    },
    files: {
      type: Array,
      required: true
    },
    propPage: {
      type: Number,
      required: false,
      default: 1
    }
  },

  data: () => ({
    page: 1,
    gpx: { loading: false, features: null, error: '' },
    gpxRequestId: 0
  }),

  computed: {
    current () {
      return this.files[this.page - 1] ?? this.files[0]
    }
  },

  watch: {
    current: {
      handler (file) {
        if (file?.isGpx) this.loadGpx(file)
      },
      immediate: true
    }
  },

  created () {
    this.page = this.propPage
  },

  methods: {
    previous () {
      this.page = this.page > 1 ? this.page - 1 : this.files.length
    },

    next () {
      this.page = this.page < this.files.length ? this.page + 1 : 1
    },

    async download (file) {
      const fetchedFile = await fetch(file.ref)
      saveAs(await fetchedFile.blob(), file.name)
    },

    async loadGpx (file) {
      const requestId = ++this.gpxRequestId
      this.gpx = { loading: true, features: null, error: '' }
      try {
        const response = await fetch(file.ref)
        const geojson = gpxToGeojson(await response.text())
        if (requestId !== this.gpxRequestId) return
        if (!geojson) {
          this.gpx = { loading: false, features: null, error: this.$t('gpxUnreadable') }
        } else if (geojson.features.length === 0) {
          this.gpx = { loading: false, features: null, error: this.$t('gpxNoData') }
        } else {
          this.gpx = { loading: false, features: geojson, error: '' }
        }
      } catch (e) {
        console.error(e)
        if (requestId !== this.gpxRequestId) return
        this.gpx = { loading: false, features: null, error: this.$t('gpxUnreadable') }
      }
    }
  }
}
</script>

<style scoped>
.evidence-stage ::v-deep .v-card {
  background-color: transparent !important;
}

.evidence-nav {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 12px;
  z-index: 30;
}

.evidence-page {
  color: #F9F6DF;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}
</style>
